import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Server, Database, MapPin, Zap, Trash2, RefreshCw, Shield, BarChart3, AlertCircle, CheckCircle, XCircle, Activity, ChevronDown, ChevronUp, BookOpen, Sparkles, Loader2, Eye, PlayCircle } from 'lucide-react';
import { fetchSystemStatus, fetchCacheStats, invalidateCache, fetchDataQuality, triggerNormalize } from '@/services/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useUserStore } from '@/store/userStore';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ITEMS = [
  { key: 'api', label: 'API Backend', icon: Server },
  { key: 'db', label: 'Base de datos (MongoDB)', icon: Database },
  { key: 'arcgis', label: 'ArcGIS Feature Services', icon: MapPin },
];

const DQ_MODULES = [
  { key: 'ops', label: 'OPS', color: '#4A9EFF', icon: Shield },
  { key: 'liderazgo', label: 'Liderazgo', color: '#00C853', icon: BarChart3 },
  { key: 'near_misses', label: 'Near Misses', color: '#FFD600', icon: AlertCircle },
];

export function SystemTab() {
  const queryClient = useQueryClient();
  const { token } = useUserStore();
  const [catalogExpanded, setCatalogExpanded] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null); // null | {dry_run, steps, total}
  const [cleanupRunning, setCleanupRunning] = useState(false);

  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['system-status'],
    queryFn: fetchSystemStatus,
    refetchInterval: 60000,
  });
  const { data: cacheStats, refetch: refetchCache } = useQuery({
    queryKey: ['cache-stats'],
    queryFn: fetchCacheStats,
    refetchInterval: 30000,
  });
  const { data: dq, isLoading: dqLoading, refetch: refetchDQ } = useQuery({
    queryKey: ['data-quality'],
    queryFn: fetchDataQuality,
    staleTime: 60000,
  });
  const { data: catalogHealth, isLoading: catalogLoading, refetch: refetchCatalog } = useQuery({
    queryKey: ['catalog-health'],
    queryFn: async () => {
      const r = await fetch(`${API}/api/admin/catalog-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Error al cargar salud del catálogo');
      return r.json();
    },
    staleTime: 120000,
    refetchInterval: 300000,
  });

  const normalizeMut = useMutation({
    mutationFn: triggerNormalize,
    onSuccess: (res) => {
      toast.success(`Enriquecimiento completado: ${res.sede_label_enriquecida_ops ?? 0} OPS + ${res.sede_label_enriquecida_liderazgo ?? 0} Liderazgo actualizados`);
      refetchDQ();
    },
    onError: () => toast.error('Error al ejecutar la normalización'),
  });

  const invalidateCacheMutation = useMutation({
    mutationFn: invalidateCache,
    onSuccess: () => {
      toast.success('Caché invalidado correctamente');
      refetchCache();
      queryClient.invalidateQueries({ queryKey: ['admin-centros'] });
    },
    onError: () => toast.error('Error al invalidar el caché'),
  });

  const forceSyncMut = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API}/api/admin/trigger-sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Error al iniciar sync');
      return r.json();
    },
    onSuccess: (res) => toast.success(res.message || 'Sincronización iniciada'),
    onError: () => toast.error('No se pudo iniciar la sincronización'),
  });

  const dedupMut = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API}/api/admin/dedup-cache`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Error al deduplicar');
      return r.json();
    },
    onSuccess: (res) => {
      const n = res.total_deleted ?? 0;
      const d = res.detail ?? {};
      const ops_del = (d.ops_cache?.oid_duplicates_deleted ?? 0) + (d.ops_cache?.content_duplicates_deleted ?? 0);
      const lid_del = (d.liderazgo_cache?.oid_duplicates_deleted ?? 0) + (d.liderazgo_cache?.content_duplicates_deleted ?? 0);
      const nm_del  = (d.nm_cache?.oid_duplicates_deleted ?? 0) + (d.nm_cache?.content_duplicates_deleted ?? 0);
      if (n > 0) {
        toast.success(
          `Deduplicación completada: ${n.toLocaleString('es-ES')} registros duplicados eliminados (OPS: ${ops_del}, Liderazgo: ${lid_del}, NM: ${nm_del})`,
          { duration: 8000 }
        );
      } else {
        toast.info('No se encontraron duplicados — los datos ya están limpios');
      }
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
      queryClient.invalidateQueries({ queryKey: ['data-quality'] });
    },
    onError: () => toast.error('Error al ejecutar la deduplicación'),
  });

  return (
    <div className="space-y-5">
      {/* Force Sync — panel HERO prominente */}
      <div
        className="rounded-2xl p-6"
        style={{
          background: 'linear-gradient(135deg, rgba(74,158,255,0.12) 0%, rgba(0,200,83,0.06) 100%)',
          border: '1.5px solid rgba(74,158,255,0.35)',
          boxShadow: '0 0 32px rgba(74,158,255,0.08)',
        }}
        data-testid="force-sync-panel"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.3)' }}>
              <Zap size={22} style={{ color: '#4A9EFF' }} />
            </div>
            <div>
              <p className="text-base font-bold font-montserrat" style={{ color: 'var(--vz-text-primary)' }}>
                Forzar Sincronización con ArcGIS
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                Sincronización automática cada 3 h. Usa este botón si faltan registros recientes.
              </p>
            </div>
          </div>
          <Button
            size="lg"
            onClick={() => forceSyncMut.mutate()}
            disabled={forceSyncMut.isPending}
            data-testid="force-sync-btn"
            className="gap-2 font-semibold text-sm px-6 min-w-[180px] h-11 rounded-xl flex-shrink-0"
            style={{
              background: forceSyncMut.isPending ? 'rgba(74,158,255,0.1)' : 'rgba(74,158,255,0.2)',
              color: '#4A9EFF',
              border: '1.5px solid rgba(74,158,255,0.45)',
              boxShadow: forceSyncMut.isPending ? 'none' : '0 2px 12px rgba(74,158,255,0.2)',
            }}
          >
            <RefreshCw size={16} className={forceSyncMut.isPending ? 'animate-spin' : ''} />
            {forceSyncMut.isPending ? 'Sincronizando...' : 'Sincronizar ahora'}
          </Button>
        </div>
      </div>
      {/* ── Deduplication panel ── */}
      <div
        className="rounded-2xl p-5"
        style={{
          background: 'linear-gradient(135deg, rgba(229,57,53,0.10) 0%, rgba(255,152,0,0.06) 100%)',
          border: '1.5px solid rgba(229,57,53,0.30)',
        }}
        data-testid="dedup-panel"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(229,57,53,0.15)', border: '1px solid rgba(229,57,53,0.3)' }}>
              <Trash2 size={18} style={{ color: '#e53935' }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: '#e53935' }}>
                Eliminar registros duplicados
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                Detecta y elimina OPS / Liderazgo / NM duplicados en base de datos. Ejecutar tras cada redeploy si los totales son el doble de lo esperado.
              </p>
            </div>
          </div>
          <Button
            size="default"
            onClick={() => dedupMut.mutate()}
            disabled={dedupMut.isPending}
            data-testid="dedup-btn"
            className="gap-2 font-semibold text-sm px-5 min-w-[180px] h-10 rounded-xl flex-shrink-0"
            style={{
              background: dedupMut.isPending ? 'rgba(229,57,53,0.08)' : 'rgba(229,57,53,0.15)',
              color: '#e53935',
              border: '1.5px solid rgba(229,57,53,0.40)',
            }}
          >
            <Trash2 size={14} className={dedupMut.isPending ? 'animate-pulse' : ''} />
            {dedupMut.isPending ? 'Deduplicando...' : 'Deduplicar ahora'}
          </Button>
        </div>
      </div>

      {/* Service status */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-montserrat text-xs uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>Estado del sistema</p>
          <Button variant="ghost" size="sm" className="text-xs h-7 gap-1.5" onClick={() => refetch()} style={{ color: 'var(--vz-text-secondary)' }}>
            <RefreshCw size={11} /> Actualizar
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {STATUS_ITEMS.map(({ key, label, icon: Icon }) => {
            const isOk = status?.[key] === 'ok';
            return (
              <div key={key} className="p-4 rounded-xl border flex items-center gap-3"
                data-testid={`system-status-${key}`}
                style={{
                  background: 'var(--vz-bg-elevated)',
                  borderColor: isLoading ? 'var(--vz-border-subtle)' : isOk ? 'rgba(0,200,83,0.25)' : 'rgba(255,82,82,0.25)',
                }}
              >
                {isLoading
                  ? <div className="w-4 h-4 border-2 border-[#4A9EFF] border-t-transparent rounded-full animate-spin" />
                  : isOk ? <CheckCircle size={18} style={{ color: '#00C853' }} />
                  : <XCircle size={18} style={{ color: '#FF5252' }} />}
                <div>
                  <p className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{label}</p>
                  <p className="text-[10px] font-mono" style={{ color: isOk ? '#00C853' : '#FF5252' }}>
                    {isLoading ? 'Verificando…' : isOk ? 'Operativo' : (status?.[key] || 'Error')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        {status?.timestamp && (
          <p className="text-[10px] font-mono mt-2" style={{ color: 'var(--vz-text-muted)' }}>
            Última verificación: {new Date(status.timestamp).toLocaleString('es-ES')}
          </p>
        )}
      </div>

      {/* Cache stats */}
      <div className="p-4 rounded-xl border space-y-3" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
        <div className="flex items-center justify-between">
          <p className="font-montserrat text-[10px] uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--vz-text-muted)' }}>
            <Zap size={12} style={{ color: '#FFD600' }} /> Caché de consultas ArcGIS
          </p>
          <Button
            variant="ghost" size="sm" className="text-xs h-7 gap-1.5"
            style={{ color: '#FF6B35', borderColor: 'rgba(255,107,53,0.3)', border: '1px solid' }}
            onClick={() => invalidateCacheMutation.mutate()}
            disabled={invalidateCacheMutation.isPending}
            data-testid="cache-invalidate-button"
          >
            {invalidateCacheMutation.isPending
              ? <div className="w-3 h-3 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
              : <Trash2 size={11} />}
            Limpiar caché
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Entradas en caché', value: cacheStats?.total_entries ?? '—', color: '#4A9EFF' },
            { label: 'Entradas activas', value: cacheStats?.alive_entries ?? '—', color: '#00C853' },
            { label: 'TTL predeterminado', value: cacheStats ? `${cacheStats.cache_ttl_seconds}s` : '—', color: '#FFD600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="text-center p-2 rounded-lg" style={{ background: 'var(--vz-bg-card)' }}>
              <p className="text-lg font-bold font-mono" style={{ color }}>{value}</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Salud del Catálogo de Centros ─────────────────────────────── */}
      {(() => {
        const sinDatos = catalogHealth?.sin_datos ?? 0;
        const total = catalogHealth?.total_catalog ?? 0;
        const pct = catalogHealth?.health_pct ?? 0;
        const orphans = catalogHealth?.orphan_count ?? 0;
        const isAlert = sinDatos > 0 || orphans > 0;
        const alertColor = sinDatos > 20 ? '#FF5252' : sinDatos > 5 ? '#FFD600' : '#00C853';

        return (
          <div
            className="p-4 rounded-xl border space-y-4"
            style={{
              background: 'var(--vz-bg-elevated)',
              borderColor: isAlert && !catalogLoading ? `${alertColor}44` : 'var(--vz-border-subtle)',
            }}
            data-testid="catalog-health-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-montserrat text-[10px] uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--vz-text-muted)' }}>
                  <BookOpen size={12} style={{ color: '#4A9EFF' }} /> Salud del catálogo de centros
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                  Centros en catálogo con / sin datos en ArcGIS
                </p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-7 gap-1.5" onClick={() => refetchCatalog()} style={{ color: 'var(--vz-text-secondary)' }}>
                <RefreshCw size={11} /> Actualizar
              </Button>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Total en catálogo', value: catalogLoading ? '…' : total, color: '#4A9EFF' },
                { label: 'Con datos ArcGIS', value: catalogLoading ? '…' : catalogHealth?.con_datos ?? 0, color: '#00C853' },
                { label: 'Sin ningún dato', value: catalogLoading ? '…' : sinDatos, color: sinDatos > 0 ? alertColor : '#00C853' },
                { label: 'Huérfanos GIS', value: catalogLoading ? '…' : orphans, color: orphans > 0 ? '#FFD600' : '#00C853', tooltip: 'Aparecen en ArcGIS pero no en el catálogo' },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center p-2.5 rounded-lg" style={{ background: 'var(--vz-bg-card)' }}>
                  <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
                  <p className="text-[10px] mt-0.5 leading-tight" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            {!catalogLoading && total > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                  <span>Cobertura de datos</span>
                  <span className="font-mono font-bold" style={{ color: alertColor }}>{pct}%</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--vz-bg-card)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${alertColor}88, ${alertColor})` }}
                  />
                </div>
              </div>
            )}

            {/* Orphan alert */}
            {!catalogLoading && orphans > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(255,214,0,0.08)', border: '1px solid rgba(255,214,0,0.25)' }}>
                <AlertCircle size={14} style={{ color: '#FFD600', flexShrink: 0, marginTop: 1 }} />
                <p className="text-[11px]" style={{ color: '#FFD600' }}>
                  <strong>{orphans}</strong> código(s) existen en ArcGIS pero aún no están en el catálogo: {(catalogHealth?.orphan_codes || []).join(', ')}. Se añadirán automáticamente en la próxima sincronización.
                </p>
              </div>
            )}

            {/* Expandable list of centers without data */}
            {!catalogLoading && sinDatos > 0 && (
              <div>
                <button
                  onClick={() => setCatalogExpanded(v => !v)}
                  className="w-full flex items-center justify-between p-2.5 rounded-lg text-xs font-medium transition-colors"
                  style={{ background: `${alertColor}0f`, color: alertColor, border: `1px solid ${alertColor}33` }}
                  data-testid="catalog-health-expand-btn"
                >
                  <span className="flex items-center gap-2">
                    <AlertCircle size={13} />
                    {sinDatos} centro(s) sin ningún registro en ArcGIS
                  </span>
                  {catalogExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {catalogExpanded && (
                  <div className="mt-2 rounded-lg overflow-hidden border" style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid="catalog-health-list">
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead style={{ background: 'var(--vz-bg-card)', position: 'sticky', top: 0 }}>
                          <tr>
                            <th className="text-left py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Código</th>
                            <th className="text-left py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Nombre</th>
                            <th className="text-left py-2 px-3 font-semibold hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>Delegación</th>
                            <th className="text-left py-2 px-3 font-semibold hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>Gerente</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(catalogHealth?.centros_sin_datos || []).map((c, i) => (
                            <tr
                              key={c.codigo}
                              style={{
                                background: i % 2 === 0 ? 'var(--vz-bg-elevated)' : 'var(--vz-bg-card)',
                                borderTop: '1px solid var(--vz-border-subtle)',
                              }}
                            >
                              <td className="py-1.5 px-3 font-mono" style={{ color: 'var(--vz-text-secondary)' }}>{c.codigo}</td>
                              <td className="py-1.5 px-3 font-medium" style={{ color: 'var(--vz-text-primary)' }}>{c.nombre}</td>
                              <td className="py-1.5 px-3 hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>{c.delegacion || '—'}</td>
                              <td className="py-1.5 px-3 hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>{c.gerente || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {catalogHealth?.generated_at && (
              <p className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                Actualizado: {new Date(catalogHealth.generated_at).toLocaleString('es-ES')}
              </p>
            )}
          </div>
        );
      })()}

      {/* Data Quality */}
      <div className="p-4 rounded-xl border space-y-4" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }} data-testid="data-quality-panel">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-montserrat text-[10px] uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--vz-text-muted)' }}>
              <Activity size={12} style={{ color: '#4A9EFF' }} /> Calidad de datos ArcGIS
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>Cobertura de sede y gerente en MongoDB</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1.5" onClick={() => refetchDQ()} style={{ color: 'var(--vz-text-secondary)' }}>
              <RefreshCw size={11} /> Actualizar
            </Button>
            <Button
              size="sm" className="text-xs h-7 gap-1.5 font-medium"
              style={{ background: 'rgba(74,158,255,0.15)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.3)' }}
              onClick={() => normalizeMut.mutate()}
              disabled={normalizeMut.isPending}
              data-testid="enrich-now-button"
            >
              {normalizeMut.isPending
                ? <div className="w-3 h-3 border-2 border-[#4A9EFF] border-t-transparent rounded-full animate-spin" />
                : <Zap size={11} />}
              Enriquecer ahora
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {DQ_MODULES.map(({ key, label, color, icon: Icon }) => {
            const stats = dq?.[key];
            const pct = stats?.pct_sede ?? 0;
            const statusColor = pct >= 90 ? '#00C853' : pct >= 70 ? '#FFD600' : '#FF5252';
            const statusLabel = pct >= 90 ? 'Excelente' : pct >= 70 ? 'Mejorable' : 'Crítico';
            return (
              <div key={key} className="p-3 rounded-lg space-y-2"
                style={{ background: 'var(--vz-bg-card)', border: `1px solid ${statusColor}22` }}
                data-testid={`dq-module-${key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon size={14} style={{ color }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{label}</span>
                    {!dqLoading && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: `${statusColor}18`, color: statusColor }}>{statusLabel}</span>
                    )}
                  </div>
                  {!dqLoading && (
                    <span className="text-sm font-bold font-mono" style={{ color: statusColor }}>{pct}%</span>
                  )}
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--vz-bg-elevated)' }}>
                  {!dqLoading && (
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${statusColor}99, ${statusColor})` }}
                    />
                  )}
                </div>
                {!dqLoading && stats && (
                  <div className="flex items-center gap-4 pt-0.5">
                    <span className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                      Total: <span style={{ color: 'var(--vz-text-secondary)' }}>{stats.total.toLocaleString('es-ES')}</span>
                    </span>
                    <span className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                      Con sede: <span style={{ color: '#00C853' }}>{stats.with_sede.toLocaleString('es-ES')}</span>
                    </span>
                    {stats.without_sede > 0 && (
                      <span className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                        Sin sede: <span style={{ color: '#FF5252' }}>{stats.without_sede.toLocaleString('es-ES')}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {dq?.generated_at && (
          <p className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
            Actualizado: {new Date(dq.generated_at).toLocaleString('es-ES')}
          </p>
        )}
      </div>

      {/* ── LIMPIEZA COMPLETA DE BD ────────────────────────────────────────── */}
      <div className="p-4 rounded-xl border space-y-4" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'rgba(255,82,82,0.2)' }} data-testid="cleanup-panel">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-montserrat text-[10px] uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--vz-text-muted)' }}>
              <Trash2 size={12} style={{ color: '#FF5252' }} /> Limpieza completa de base de datos
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
              Elimina registros vacíos, usuarios de prueba, reglas RLS de test y overrides en blanco.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              className="h-8 text-xs gap-1.5"
              style={{ background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.3)', color: '#4A9EFF' }}
              disabled={cleanupRunning}
              onClick={async () => {
                setCleanupRunning(true);
                setCleanupResult(null);
                try {
                  const r = await fetch(`${API}/api/admin/cleanup-preview`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const d = await r.json();
                  setCleanupResult(d);
                  toast.info(`Simulación: se eliminarían ${d.total} registros`);
                } catch { toast.error('Error al simular limpieza'); }
                finally { setCleanupRunning(false); }
              }}
              data-testid="btn-cleanup-preview"
            >
              {cleanupRunning ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
              Simular
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs gap-1.5"
              style={{ background: 'rgba(255,82,82,0.12)', border: '1px solid rgba(255,82,82,0.3)', color: '#FF5252' }}
              disabled={cleanupRunning}
              onClick={async () => {
                if (!window.confirm('¿Confirmas la limpieza completa? Esta operación no se puede deshacer.')) return;
                setCleanupRunning(true);
                setCleanupResult(null);
                try {
                  const r = await fetch(`${API}/api/admin/cleanup-full`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const d = await r.json();
                  setCleanupResult(d);
                  toast.success(`Limpieza completada: ${d.total} registros eliminados`);
                  queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
                  queryClient.invalidateQueries({ queryKey: ['data-quality'] });
                } catch { toast.error('Error durante la limpieza'); }
                finally { setCleanupRunning(false); }
              }}
              data-testid="btn-cleanup-execute"
            >
              {cleanupRunning ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />}
              Ejecutar limpieza
            </Button>
          </div>
        </div>

        {cleanupResult && (
          <div className="space-y-2 pt-1 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
            <div className="flex items-center gap-2 pb-1">
              <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: cleanupResult.dry_run ? '#4A9EFF' : '#FF5252' }}>
                {cleanupResult.dry_run ? 'SIMULACIÓN — no se eliminó nada' : 'EJECUTADO — registros eliminados'}
              </span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(255,82,82,0.12)', color: '#FF5252' }}>
                Total: {cleanupResult.total}
              </span>
            </div>
            {Object.entries(cleanupResult.steps || {}).map(([key, step]) => {
              const count = typeof step === 'object' ? step.count : step;
              const desc = typeof step === 'object' ? step.description : key;
              const emails = typeof step === 'object' ? step.emails : null;
              const hasIssues = count > 0;
              return (
                <div key={key} className="flex items-start justify-between gap-2 py-1.5 px-2 rounded-lg"
                  style={{ background: hasIssues ? 'rgba(255,82,82,0.06)' : 'rgba(0,200,83,0.04)', border: `1px solid ${hasIssues ? 'rgba(255,82,82,0.15)' : 'rgba(0,200,83,0.1)'}` }}>
                  <div className="flex-1">
                    <p className="text-[10px]" style={{ color: 'var(--vz-text-secondary)' }}>{desc}</p>
                    {emails && emails.length > 0 && (
                      <p className="text-[9px] font-mono mt-0.5" style={{ color: '#FF7043' }}>{emails.join(', ')}</p>
                    )}
                  </div>
                  <span className="text-[10px] font-bold font-mono shrink-0"
                    style={{ color: hasIssues ? '#FF5252' : '#00C853' }}>
                    {hasIssues ? count : '✓'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
